/**
 * Post-payment screen (GH-230, SPEC.md §5.1 steps 9–10; account-aware rework owner-directed
 * 2026-08-17).
 *
 * Two payment states, and the distinction is the point:
 *
 *   **Processing** — the payment is authorized but no verified provider callback has confirmed
 *   it. The copy says so plainly and tells the donor not to pay again. It must never look final.
 *
 *   **Received** — a receipt exists. It is shown immediately, in full, with nothing to agree to.
 *
 * What happens *after* the receipt depends on who is standing there:
 *
 *   - A donor with a remembered session is never asked for a name or an email again. Their gift
 *     is already linked to their profile (the intent carried their token), so the page says the
 *     receipt is saved in their account, checks them in to this event if they were not already,
 *     and shows their lifetime giving — an aggregate their phone-verified session is entitled to.
 *   - A guest may leave a name, and an email or a mobile number for the receipt. Email is
 *     recorded as-is (unverified, so it earns no account claims and no totals). A mobile number
 *     goes through the SMS code — the same proof sign-in uses — and comes out the other side as
 *     a signed-in donor: receipt attached, checked in, totals shown.
 *
 * Giving history is the protected surface (SPEC.md §12.3): totals appear only behind a
 * phone-verified session, never because someone typed an address into a form.
 */
import {
  CONSENT_LABELS,
  cardFailureMessage,
  formatMoney,
  type ConsentPurposeWire,
  type DonationStatus,
  type DonorGivingSummary,
} from "@givehere/contracts";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDialogFocus } from "@givehere/ui";
import { Explain } from "./Explain.js";
import { ReceiptShare } from "./ShareControls.js";
import {
  SYNTHETIC_PAYMENTS,
  confirmSyntheticPayment,
  enrichReceipt,
  fetchDonationStatus,
  fetchGivingSummary,
  saveHeldReceiptEmail,
} from "./api.js";
import {
  confirmDonorSignIn,
  donorCheckIn,
  fetchDonorSession,
  rememberToken,
  startDonorSignIn,
  storedToken,
} from "./donor-session.js";
import { isSignUpRefusal } from "./sign-up-access.js";

const POLL_MS = 2500;

/** The purposes offered here. Deliberately few — a wall of checkboxes is not consent. */
const OFFERED_CONSENTS: readonly ConsentPurposeWire[] = ["organization_updates", "future_giving"];

/** What the page knows about the person once their session (existing or new) is resolved. */
type AccountState = {
  readonly phoneLast4: string | null;
  /** True only when *this* page moved their check-in to this event. */
  readonly checkedInNow: boolean;
  readonly summary: DonorGivingSummary | null;
};

export function ReceiptScreen({
  donationId,
  accessToken,
  eventId = null,
  bankPending = false,
  onBackToEvent = null,
  organizationId = null,
}: {
  donationId: string;
  accessToken: string;
  /** The organization given to, for "Share with friends" (GH-864e). Null draws no share control. */
  organizationId?: string | null;
  /** The event this gift was made at, for the automatic check-in. */
  eventId?: string | null;
  /** A bank (ACH) gift in flight: the waiting copy must speak in business days, not seconds. */
  bankPending?: boolean;
  /** Returns to the event home (the give page) without losing the checkout context. */
  onBackToEvent?: (() => void) | null;
}) {
  const [status, setStatus] = useState<DonationStatus | null>(null);
  const [error, setError] = useState<string | null>(null);

  /**
   * null → guest; "resolving" → a remembered token is being checked; object → signed in.
   * Resolution only starts once the receipt is actually received, so the check-in and the
   * lifetime total always include this gift.
   */
  const [account, setAccount] = useState<AccountState | "resolving" | null>(
    storedToken() ? "resolving" : null,
  );

  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [consents, setConsents] = useState<ConsentPurposeWire[]>([]);
  const [saving, setSaving] = useState(false);
  const [savedTo, setSavedTo] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  /** The SMS step, when a guest chose to verify their number. */
  const [challenge, setChallenge] = useState<{
    verificationId: string;
    phone: string;
    developmentCode: string | null;
  } | null>(null);
  const [code, setCode] = useState("");

  // Poll until the provider confirms. Stops as soon as the donation reaches a terminal state,
  // so a completed gift does not keep a phone awake on an organization's wifi.
  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const tick = async (): Promise<void> => {
      try {
        const next = await fetchDonationStatus(donationId, accessToken);
        if (cancelled) return;
        setStatus(next);
        // DEC-056: an end-of-day card gift is approved and waits for the organization's midnight;
        // polling every few seconds until then would only keep a phone awake. Stop and say so.
        if (next.displayStatus === "processing" && !next.heldForEndOfDay) {
          timer = setTimeout(() => void tick(), POLL_MS);
        }
      } catch (caught: unknown) {
        if (!cancelled)
          setError(caught instanceof Error ? caught.message : "Could not load your gift.");
      }
    };

    void tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [donationId, accessToken]);

  const received = status?.displayStatus === "received";

  /**
   * Turns a live session token into the signed-in view: check in to this event when not already
   * there (best-effort — an ended event simply keeps the old check-in), then load the aggregate.
   */
  const resolveAccount = useCallback(
    async (token: string): Promise<void> => {
      const session = await fetchDonorSession(token);
      let checkedInNow = false;
      if (eventId && session.checkIn?.eventId !== eventId) {
        checkedInNow = await donorCheckIn(token, eventId).then(
          () => true,
          () => false,
        );
      }
      const summary = await fetchGivingSummary(token).catch(() => null);
      setAccount({ phoneLast4: session.profile.phoneLast4, checkedInNow, summary });
    },
    [eventId],
  );

  // Ran-once guard: the poll effect updates `status` repeatedly and account resolution has
  // side effects (a check-in write), so it must fire a single time per receipt.
  const resolved = useRef(false);
  useEffect(() => {
    if (!received || resolved.current) return;
    const token = storedToken();
    if (!token) {
      setAccount(null);
      return;
    }
    resolved.current = true;
    void resolveAccount(token).catch(() => setAccount(null));
  }, [received, resolveAccount]);

  /** Guest path A: email (and/or name) only — recorded, no verification, no account claims. */
  const saveContact = useCallback(async () => {
    if (!status?.receipt) return;
    setSaving(true);
    setSaveError(null);
    try {
      if (phone.trim()) {
        // Path B: the number is proven before anything is claimed for it.
        const started = await startDonorSignIn(phone.trim());
        setChallenge({
          verificationId: started.verificationId,
          phone: phone.trim(),
          developmentCode: started.developmentCode,
        });
        return;
      }
      await enrichReceipt(status.receipt.receiptId, accessToken, {
        ...(displayName.trim() ? { displayName: displayName.trim() } : {}),
        ...(email.trim() ? { email: email.trim() } : {}),
        ...(consents.length ? { consents } : {}),
      });
      setSavedTo(email.trim() || null);
    } catch (caught: unknown) {
      setSaveError(caught instanceof Error ? caught.message : "Could not save those details.");
    } finally {
      setSaving(false);
    }
  }, [status, accessToken, displayName, email, phone, consents]);

  /** Guest path B, step 2: the code came back — sign in, attach, check in, show totals. */
  const confirmCode = useCallback(async () => {
    if (!challenge || !status?.receipt) return;
    setSaving(true);
    setSaveError(null);
    try {
      let session;
      try {
        session = await confirmDonorSignIn({
          verificationId: challenge.verificationId,
          code: code.trim(),
          phone: challenge.phone,
        });
      } catch (caught: unknown) {
        if (!isSignUpRefusal(caught)) throw caught;
        /*
          GH-871b (DEC-052): signing up is by invite while membership is on, and this number has no
          account. The owner's words: "email or sms receipts can be saved to contact" — so the
          receipt is saved to the number, without an account, exactly as an email is in path A.
        */
        await enrichReceipt(status.receipt.receiptId, accessToken, {
          phone: challenge.phone,
          ...(displayName.trim() ? { displayName: displayName.trim() } : {}),
          ...(email.trim() ? { email: email.trim() } : {}),
          ...(consents.length ? { consents } : {}),
        });
        setChallenge(null);
        setSavedTo(email.trim() || challenge.phone);
        return;
      }
      rememberToken(session.token);
      // Attach this gift (and anything else offered) to the now-proven profile. The server
      // matches by phone fingerprint, so this lands on the same profile sign-in just used.
      await enrichReceipt(status.receipt.receiptId, accessToken, {
        phone: challenge.phone,
        ...(displayName.trim() ? { displayName: displayName.trim() } : {}),
        ...(email.trim() ? { email: email.trim() } : {}),
        ...(consents.length ? { consents } : {}),
      });
      setChallenge(null);
      await resolveAccount(session.token);
    } catch (caught: unknown) {
      setSaveError(caught instanceof Error ? caught.message : "That code did not work.");
    } finally {
      setSaving(false);
    }
  }, [challenge, code, status, accessToken, displayName, email, consents, resolveAccount]);

  if (error) {
    return (
      <main className="card">
        <p className="notice notice--error" role="alert">
          {error}
        </p>
      </main>
    );
  }

  if (!status) return <p className="notice">Loading your gift…</p>;

  if (status.displayStatus === "failed") {
    return (
      <main className="card" role="alert">
        <h1 className="screen-title">Payment did not go through</h1>
        {status.failureReason ? (
          <p className="notice notice--error">
            {cardFailureMessage(status.failureReason, "donor")}
          </p>
        ) : null}
        <Explain label="You were not charged" title="What happens next">
          <p>You have not been charged. You can try again with another payment method.</p>
        </Explain>
        <button type="button" className="pay" onClick={onBackToEvent ?? undefined}>
          Try again
        </button>
      </main>
    );
  }

  if (status.displayStatus === "processing" && status.heldForEndOfDay) {
    return (
      <HeldForEndOfDay
        organizationName={status.organizationDisplayName}
        donationId={donationId}
        accessToken={accessToken}
        askForEmail={account === null}
      />
    );
  }

  if (status.displayStatus === "processing") {
    return (
      <ProcessingDialog
        bankPending={bankPending}
        donationId={donationId}
        accessToken={accessToken}
        onBackToEvent={onBackToEvent ?? null}
      />
    );
  }

  const receipt = status.receipt;
  if (!receipt) return <p className="notice">Loading your receipt…</p>;

  const signedIn = account !== null && account !== "resolving" ? account : null;
  /**
   * A gift held for an organization that had not claimed its listing (GH-798, DEC-046 answer 4).
   * The receipt names the configured recipient and carries the server's notation; it never says
   * the organization received anything, because until it claims or the check is mailed it has not.
   */
  const held = receipt.heldGift;
  const receivedBy =
    held?.recipientName ?? receipt.platform?.legalName ?? receipt.organizationLegalName;

  return (
    <main className="card">
      <header>
        <p className="badge">Received</p>
        <h1 className="screen-title">Thank you</h1>
        {held !== null ? (
          <p className="event-name">Your gift to {held.recipientName}</p>
        ) : (
          <p className="event-name">
            Your gift in support of {receipt.organizationDisplayName} — {receipt.eventName}
          </p>
        )}
      </header>

      <section className="totals" aria-label="Receipt">
        <div className="row">
          <span>Receipt</span>
          <span>{receipt.receiptNumber}</span>
        </div>
        <div className="row">
          <span>Donation</span>
          <span>{formatMoney(receipt.donation)}</span>
        </div>
        {BigInt(receipt.costSupport.amountMinor) > 0n && (
          <div className="row">
            <span>{receipt.costSupportLabel ?? "Processing fee"}</span>
            <span>{formatMoney(receipt.costSupport)}</span>
          </div>
        )}
        <div className="row row--total">
          <span>Total</span>
          <span>{formatMoney(receipt.total)}</span>
        </div>
        <div className="row">
          {/*
            The donee, not the organization. The acknowledgment beside this already names the
            Foundation under DEC-024; a line reading "Received by [organization]" contradicted it, and
            of the two contradictory sentences the wrong one is the evidence Rev. Rul. 62-113
            looks for. Falls back to the organization only where no platform identity is set,
            which is a deployment that has not been configured rather than a normal one.
          */}
          <span className="muted small">Received by {receivedBy}</span>
          <span className="muted small">{new Date(receipt.issuedAt).toLocaleDateString()}</span>
        </div>
      </section>

      {/* The server's notation, verbatim: the recipient, and the organization it is targeted for. */}
      {held !== null && <p className="notice">{held.notation}</p>}

      {/*
        A refund (GH-840), verbatim from the server: when, how much went back, and the platform
        fee kept. The receipt above is unchanged — receipts are never edited; this is beside it.
      */}
      {receipt.refund && (
        <p className="notice" role="status">
          {receipt.refund.notation}
        </p>
      )}

      {/*
        The §170(f)(8) acknowledgment, verbatim — and the first time a donor on this surface has
        been able to read it.

        The server has composed it on every receipt since DEC-024
        (`packages/domain/src/tax-documents.ts`), and no client drew it: not this page, not the iOS
        receipt, and not `renderReceiptMessage`, which composes the emailed one. So a contemporaneous
        written acknowledgment existed in every API response and in nothing a person could hold.

        Every line is the server's, in the order it sent them. Nothing is added around them and
        nothing is reworded: an acknowledgment is a legal document whose wording is counsel's, and
        a heading of our own — "For your taxes", say — would be a deduction claim made by a UI
        decision rather than by counsel (DEC-011). `aria-label` names the section without asserting
        anything about it.
      */}
      {receipt.acknowledgment.length > 0 && (
        <section className="acknowledgment" aria-label="Acknowledgment">
          {receipt.acknowledgment.map((line) => (
            <p key={line}>{line}</p>
          ))}
        </section>
      )}

      {/* Signed in: everything is already where it belongs, and the page says so. */}
      {signedIn && (
        <section aria-live="polite">
          <p className="notice" role="status">
            This receipt is saved in your account
            {signedIn.phoneLast4 ? ` (mobile ····${signedIn.phoneLast4})` : ""}.
          </p>
          {signedIn.checkedInNow && (
            <p className="notice" role="status">
              We&apos;ve checked you in to {receipt.eventName}.
            </p>
          )}
          {signedIn.summary && (
            <p className="muted">
              {BigInt(signedIn.summary.totalGiven.amountMinor) > 0n
                ? `You've given ${formatMoney(signedIn.summary.totalGiven)} across ${signedIn.summary.giftCount} ${signedIn.summary.giftCount === 1 ? "gift" : "gifts"} — all organizations, all time.`
                : "Your first gift — thank you."}
            </p>
          )}
        </section>
      )}

      {/* Guest, SMS step: the code was texted; prove the number to finish. */}
      {!signedIn && challenge && (
        <section>
          <h2 className="section-heading">Check your messages</h2>
          <p className="muted small">
            We texted a code to your number ending {challenge.phone.slice(-4)}. Enter it to save
            this receipt to your account.
          </p>
          {challenge.developmentCode && (
            <p className="muted small">Development code: {challenge.developmentCode}</p>
          )}
          <label className="field">
            <span className="visually-hidden">Verification code</span>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              autoFocus
              value={code}
              onChange={(changed) => setCode(changed.target.value)}
            />
          </label>
          {saveError && (
            <p className="notice notice--error" role="alert">
              {saveError}
            </p>
          )}
          <button
            type="button"
            className="pay"
            disabled={saving || code.trim().length < 4}
            onClick={() => void confirmCode()}
          >
            {saving ? "Checking…" : "Verify"}
          </button>
          <button
            type="button"
            className="link link--center"
            onClick={() => {
              setChallenge(null);
              setCode("");
              setSaveError(null);
            }}
          >
            Use a different contact
          </button>
        </section>
      )}

      {/* Guest, nothing saved yet: optional name, and either channel for the receipt. */}
      {!signedIn && !challenge && !savedTo && account !== "resolving" && (
        <section>
          <h2 className="section-heading">Optional — keep this receipt</h2>
          <Explain
            className="muted small"
            label="Your gift is complete"
            title="Saving your receipt"
          >
            <p>
              Leave an email or mobile number if you want your receipt saved for you — a mobile
              number verifies by text and shows your giving to date.
            </p>
          </Explain>

          <label className="field">
            <span>Your name</span>
            <input
              type="text"
              maxLength={80}
              value={displayName}
              onChange={(changed) => setDisplayName(changed.target.value)}
            />
          </label>

          <label className="field">
            <span>Email for your receipt</span>
            <input
              type="email"
              inputMode="email"
              autoComplete="email"
              value={email}
              onChange={(changed) => setEmail(changed.target.value)}
            />
          </label>

          <label className="field">
            <span>Or your mobile number</span>
            <input
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              value={phone}
              onChange={(changed) => setPhone(changed.target.value)}
            />
          </label>

          <fieldset>
            <legend className="visually-hidden">Optional updates</legend>
            {OFFERED_CONSENTS.map((purpose) => (
              <label className="toggle" key={purpose}>
                <input
                  type="checkbox"
                  checked={consents.includes(purpose)}
                  onChange={() =>
                    setConsents((current) =>
                      current.includes(purpose)
                        ? current.filter((item) => item !== purpose)
                        : [...current, purpose],
                    )
                  }
                />
                <span>{CONSENT_LABELS[purpose]}</span>
              </label>
            ))}
          </fieldset>

          {saveError && (
            <p className="notice notice--error" role="alert">
              {saveError}
            </p>
          )}

          <button
            type="button"
            className="pay"
            disabled={
              saving ||
              (!displayName.trim() && !email.trim() && !phone.trim() && consents.length === 0)
            }
            onClick={() => void saveContact()}
          >
            {saving ? "Saving…" : phone.trim() ? "Text me a code" : "Save"}
          </button>
        </section>
      )}

      {/* Guest, email-only save: recorded against the address, honestly described. */}
      {!signedIn && savedTo && (
        <p className="notice" role="status">
          Saved. Your receipt is on record for {savedTo}.
        </p>
      )}

      {/*
        Secondary and last-but-one (GH-864e): only once the gift is received — never before or
        during payment — and never for a gift held for an unclaimed organization, which has no page
        of its own to share yet. Hidden while membership is off.
      */}
      {organizationId && held === null && <ReceiptShare organizationId={organizationId} />}

      {onBackToEvent && (
        <button type="button" className="pay pay--secondary" onClick={onBackToEvent}>
          Back to {receipt.eventName}
        </button>
      )}

      {receipt.supportContact && (
        <p className="muted small">
          Questions about this gift, or need a refund? Contact {receipt.supportContact} and quote{" "}
          {receipt.receiptNumber}.
        </p>
      )}
    </main>
  );
}

/**
 * An end-of-day card gift (DEC-056): the card is approved, and the organization charges its gifts
 * after its own midnight. Calm and final for tonight — no spinner, because nothing more happens
 * while the donor watches — and never a card detail. The organization's name is the tenant's own;
 * nothing here assumes what kind of organization it is.
 */
function HeldForEndOfDay({
  organizationName,
  donationId,
  accessToken,
  askForEmail,
}: {
  organizationName: string;
  donationId: string;
  accessToken: string;
  /** A guest only: a signed-in donor's receipt already goes to the address on file. */
  askForEmail: boolean;
}) {
  const [email, setEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const [savedTo, setSavedTo] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const save = async (): Promise<void> => {
    setSaving(true);
    setSaveError(null);
    try {
      await saveHeldReceiptEmail(donationId, accessToken, { email: email.trim() });
      setSavedTo(email.trim());
    } catch (caught: unknown) {
      setSaveError(caught instanceof Error ? caught.message : "Could not save that address.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <main className="card" role="status" aria-live="polite">
      <header>
        <p className="badge">Approved</p>
        <h1 className="screen-title">Thank you</h1>
        <p className="event-name" dir="auto">
          Your gift to {organizationName}
        </p>
      </header>
      <Explain label="Charged at day's end" title="Your gift is approved">
        <p>
          Your gift is approved. <bdi>{organizationName}</bdi> finalizes gifts at the end of the
          day, so your card is charged then and your receipt is issued at that time. You do not need
          to give again.
        </p>
      </Explain>

      {/* GH-947: the receipt is issued at the day's end; a guest can say where to send it now. */}
      {askForEmail && savedTo && (
        <p className="notice" role="status">
          Saved. Your receipt will be emailed to {savedTo} when your gift is charged.
        </p>
      )}
      {askForEmail && !savedTo && (
        <section>
          <h2 className="section-heading">Optional — email me the receipt</h2>
          <label className="field">
            <span>Email for your receipt</span>
            <input
              type="email"
              inputMode="email"
              autoComplete="email"
              maxLength={254}
              value={email}
              onChange={(changed) => setEmail(changed.target.value)}
            />
          </label>
          {saveError && (
            <p className="notice notice--error" role="alert">
              {saveError}
            </p>
          )}
          <button
            type="button"
            className="pay"
            disabled={saving || !email.trim()}
            onClick={() => void save()}
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </section>
      )}
    </main>
  );
}

/**
 * The wait between "Pay" and the receipt.
 *
 * A spinner dialog, not a page of text (owner-directed 2026-08-17): the donor just tapped Pay, and
 * what they need is motion that says "working" and then the receipt — which `ReceiptScreen` swaps
 * in the moment the poll sees the webhook-confirmed truth.
 *
 * Its **own component**, not an early return inside `ReceiptScreen`, so that `useDialogFocus`
 * mounts and unmounts with the dialog. Inside `ReceiptScreen` the effect would run once when the
 * whole screen mounted — before this dialog existed — and never again when it appeared, which is
 * the same "declares aria-modal, does not keep it" defect the hook exists to remove.
 */
function ProcessingDialog({
  bankPending,
  donationId,
  accessToken,
  onBackToEvent,
}: {
  bankPending: boolean;
  donationId: string;
  accessToken: string;
  onBackToEvent: (() => void) | null;
}) {
  const panelRef = useDialogFocus<HTMLDivElement>();

  return (
    <div className="sheet" role="dialog" aria-modal="true" aria-label="Confirming your gift">
      <div
        className="sheet__panel processing-dialog"
        role="status"
        aria-live="polite"
        ref={panelRef}
        tabIndex={-1}
      >
        <div className="spinner" aria-hidden="true" />
        <h1 className="processing-dialog__title">
          {bankPending ? "Bank transfer on its way" : "Confirming your gift"}
        </h1>
        {/*
          The five visible words are chosen for what a donor might do wrong while reading them. On
          the confirming path that is paying twice, so "Do not pay again" is the line itself rather
          than the detail behind it — a warning one tap away is a warning that arrives after the
          second payment. On the bank path there is nothing to get wrong, so the line is the status.
        */}
        <Explain
          className="muted"
          label={bankPending ? "Authorized — a few days" : "Do not pay again"}
          title={bankPending ? "Bank transfers" : "Still confirming"}
        >
          <p>
            {bankPending
              ? "It's authorized — bank transfers take a few business days to complete. You're all set; your receipt will be here."
              : "Usually a few seconds. Do not pay again — your gift is already on its way."}
          </p>
        </Explain>
        {bankPending && onBackToEvent && (
          <button type="button" className="pay pay--secondary" onClick={onBackToEvent}>
            Back to the event
          </button>
        )}

        {SYNTHETIC_PAYMENTS && (
          <>
            <p className="muted small">
              This deployment runs on a synthetic payment rail — no real money moves and no bank is
              involved, so nothing will confirm it on its own.
            </p>
            <button
              type="button"
              className="pay"
              onClick={() => void confirmSyntheticPayment(donationId, accessToken)}
            >
              Simulate bank confirmation
            </button>
          </>
        )}
      </div>
    </div>
  );
}
